import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import spinner from '../../../telstra-ui-resources/images/Spinner.svg';
import {
  ButtonIconPosition,
  ButtonSize,
  ButtonState,
  ButtonType,
  ButtonVariant,
} from '../../common/Types';
import Icon from '../Icon/Icon';

@Component
export default class Button extends Vue {

  @Prop() label!: string;
  @Prop() type!: ButtonType;
  @Prop({ default: 'active' }) state!: ButtonState;
  @Prop() size!: ButtonSize;
  @Prop({ default: 'super' }) variant!: ButtonVariant;
  @Prop() className!: string;
  @Prop() icon!: string;
  @Prop() iconPosition!: ButtonIconPosition;
  @Prop() width!: string;
  @Prop() height!: string;
  @Prop() name!: string;
  @Prop() disabled!: boolean;
  @Prop() id!: string;

  render(): JSX.Element {
    return (
      this._buttonRender()
    );
  }

  @Emit()
  onClick(e: any): void {
    this.$emit('click', e);
  }

  @Emit()
  onMouseEnter(e: any): void {
    this.$emit('mouseEnter', e);
  }

  @Emit()
  onMouseLeave(e: any): void {
    this.$emit('mouseLeave', e);
  }

  private _iconColorFinder(state: ButtonState): string {
    let iconColorCustom: string = 'tl-button-svg-default';

    if (state === 'disabled' || this.disabled) {
      iconColorCustom = 'tl-button-svg-default tl-button-icon-color-Disabled';
    } else if (state === 'warning') {
      iconColorCustom = 'tl-button-svg-default tl-button-icon-color-Warning';
    } else {
      iconColorCustom = 'tl-button-svg-default tl-button-icon-color-Active';
    }
    return iconColorCustom;
  }

  private _createButton(buttonInnerContent: any): JSX.Element {
    const buttonClass: string = this._getButton(this.variant, this.state);
    const customWidth: string = this.width ? this.width : '';
    const customHeight: string = this.height ? this.height : '';
    const customStyle: any = { minWidth: customWidth, height: customHeight };
    const buttonType: ButtonType = this.type === 'dropdownButton' ? 'button' : this.type;
    const buttonRender: JSX.Element = <button
      class={buttonClass}
      disabled={this.disabled}
      onClick={this.onClick}
      name={this.name}
      id={this.id}
      style={customStyle}
      type={buttonType}
    >
      {buttonInnerContent}
    </button>;
    return buttonRender;
  }

  private _getButtonInnerContent(): JSX.Element {
    let buttonInnerContent: JSX.Element;
    const iconColorCustom: string = this._iconColorFinder(this.state);
    const primaryIcon: string = 'icon-system-arrow';
    const buttonText: any = this.label ? this.label : (this.$slots.default && this.$slots.default[0]) ? this.$slots.default![0].text : '';
    const variantType: string = this.variant ? this.variant : 'super';
    switch (variantType) {
      case 'super':
        if (this.icon) {
          buttonInnerContent = <div>
            {buttonText}
            <span class={'tl-button-icon'}><svg className={iconColorCustom}><Icon name={this.icon} /></svg></span>
          </div>;
          break;
        } else if (this.state === 'loading') {
          buttonInnerContent = <img class={'tl-button-text-spinner'} src={spinner} alt='' />;
          break;
        } else {
          buttonInnerContent = buttonText;
          break;
        }
      case 'primary':
        if (this.type === 'dropdownButton') {
          buttonInnerContent = this.icon ? <div>
            {this.icon ? <span class={'tl-button-icon tl-bd-icon-left'}><svg className={iconColorCustom}><Icon name={this.icon} /></svg></span> : <div></div>}
            {buttonText}
            <span class={'tl-button-icon tl-bd-icon-right'}>
              <svg id={this.name + '-svg'} className={iconColorCustom + ' tl-bd-svg-right toggle-down'}>
                <Icon name={'icon-system-chevron-up'} />
              </svg>
            </span>
          </div> : <div>
              {buttonText}
            </div>;
          break;
        } else if (this.iconPosition) {
          this.iconPosition === 'right' ?
            buttonInnerContent = <div>
              {buttonText}
              <span class={'tl-button-icon'}><svg className={iconColorCustom}><Icon name={this.icon} /></svg></span>
            </div> :
            buttonInnerContent = <div>
              <span class={'tl-button-icon'}><svg className={iconColorCustom}><Icon name={this.icon} /></svg></span>
              {buttonText}
            </div>;
          break;
        } else if (this.icon) {
          buttonInnerContent = <div>
            <span class={'tl-button-icon'}><svg className={iconColorCustom}><Icon name={this.icon} /></svg></span>
            {buttonText}
          </div>;
          break;
        } else {
          buttonInnerContent = <div>
            {buttonText}
            <span class={'tl-button-icon'}>
              <svg className={iconColorCustom}><Icon name={this.icon ? this.icon : primaryIcon} /></svg></span>
          </div>;
          break;
        }
      case 'secondary':
        buttonInnerContent = buttonText;
        break;
      default:
        buttonInnerContent = buttonText;
    }
    return buttonInnerContent;
  }

  private _buttonRender(): JSX.Element {
    const buttonRender = this._createButton(this._getButtonInnerContent());
    return buttonRender;
  }

  private _getButtonClass(variant = 'super', state = 'active'): string {
    let buttonClass: string = '';
    if (state === 'disabled' || this.disabled) {
      buttonClass = 'tl-button-' + variant + '-disabled';
    } else if (state === 'loading') {
      buttonClass = 'tl-button-' + variant + '-' + state;
    } else if (variant === 'secondary') {
      const buttonType = state === 'app' ? 'warning' : 'active';
      buttonClass = 'tl-button-' + variant + '-' + buttonType;
    } else if (state === 'warning') {
      buttonClass = 'tl-button-' + variant + '-' + state;
    } else {
      buttonClass = 'tl-button-' + variant + '-active';
    }
    if (this.className) {
      buttonClass += ' ' + this.className;
    }
    if (this.size) {
      buttonClass += ' tl-button-' + this.size;
    }
    return buttonClass;
  }

  private _getButton(variant: ButtonVariant = 'super', state: ButtonState = 'active'): string {
    let buttonClass;
    const stateVal = state ? state : 'active';

    if (variant === 'super') {
      buttonClass = this._getButtonClass('super', stateVal);
    } else if (variant === 'primary') {
      buttonClass = this._getButtonClass('primary', stateVal);
      if (this.iconPosition) {
        buttonClass += this.iconPosition === 'right' ? ' tl-button-primary-icon-right ' : ' tl-button-primary-icon-left ';
      } else if (this.type === 'dropdownButton') {
        buttonClass += ' tl-button-primary-icon-left ';
      } else {
        buttonClass += ' tl-button-primary-icon-right ';
      }
    } else if (variant === 'secondary') {
      buttonClass = this._getButtonClass('secondary', stateVal);
    }
    buttonClass = 'tl-ui ' + buttonClass;
    return buttonClass;
  }

}
